import type {FlexBenefitsQuery} from '@/graphql-types'
import exhaustiveCheck from '@helios/shared/utils/exhaustive-check'
import replacePlaceholders from '@helios/shared/utils/replace-placeholders'

export const MOBILE_BREAKPOINT = 'sm'

export enum Location {
    DENMARK = 'DENMARK',
    SWEDEN = 'SWEDEN',
    GERMANY = 'GERMANY',
    UK = 'UK',
    SPAIN = 'SPAIN',
    PORTUGAL = 'PORTUGAL',
    QUEBEC = 'QUEBEC',
    REMOTE = 'REMOTE',
}

export enum Office {
    IN_OFFICE = 'IN_OFFICE',
    REMOTE = 'REMOTE',
}

export interface LocationNames {
    denmark: string
    sweden: string
    portugal: string
    germany: string
    uk: string
    quebec: string
    spain: string
    remote: string
}

const getIntlLocaleForLocation = (location: Location) => {
    switch (location) {
        case Location.DENMARK:
            return 'da-dk'
        case Location.SWEDEN:
            return 'sv-se'
        case Location.GERMANY:
            return 'de-de'
        case Location.UK:
            return 'en-gb'
        case Location.SPAIN:
            return 'es-es'
        case Location.PORTUGAL:
            return 'pt-pt'
        case Location.QUEBEC:
            return 'en-ca'
        default:
            return new Intl.NumberFormat().resolvedOptions().locale
    }
}

export const getBaseBenefitsSectionTitle = (
    location: Location,
    office: Office | '',
    countryTitle: string,
    partialRemoteTitle: string,
    fullyRemoteTitle: string
) => {
    switch (location) {
        case Location.REMOTE:
            return fullyRemoteTitle
        default:
            switch (office) {
                case Office.REMOTE:
                    return partialRemoteTitle
                default:
                    return countryTitle
            }
    }
}

export const getBaseBenefitsContent = (
    location: Location,
    office: Office | '',
    content: FlexBenefitsQuery['prismicFlexBenefitsPage']['data']
) => {
    // For Spain we need to show a different set of parental benefits
    const parentalBenefitsContent =
        location === Location.SPAIN
            ? content.parental_benefits_spain[0]
            : content.parental_benefits[0]

    if (location === Location.REMOTE || office === Office.REMOTE) {
        return [
            location === Location.GERMANY
                ? content.base_benefits_germany_remote[0]
                : content.base_benefits_remote[0],
            content.ways_of_working_remote[0],
            parentalBenefitsContent,
        ]
    }

    return [
        location === Location.GERMANY ? content.base_benefits_germany[0] : content.base_benefits[0],
        content.ways_of_working[0],
        parentalBenefitsContent,
    ]
}

const getLocationName = (location: Location, locationNames: LocationNames) => {
    switch (location) {
        case Location.DENMARK:
            return locationNames.denmark
        case Location.SWEDEN:
            return locationNames.sweden
        case Location.GERMANY:
            return locationNames.germany
        case Location.UK:
            return locationNames.uk
        case Location.SPAIN:
            return locationNames.spain
        case Location.PORTUGAL:
            return locationNames.portugal
        case Location.QUEBEC:
            return locationNames.quebec
        case Location.REMOTE:
            return locationNames.remote
        default:
            exhaustiveCheck(location)

            throw new Error('Unknown location')
    }
}

type Values = {
    budgetTotal: number
    lunchValue: number
    healthcareValue: number
    maxAdditionalLeaveDays: number
    costPerLeaveDay: number
}

// See this Notion page for the most up-to-date values:
// https://www.notion.so/pleo/Pleo-Benefits-2-0-Hub-43c5b4ecbd5f4de6bf344ed812741941
export const getValuesForLocation = (location: Location): Values => {
    switch (location) {
        case Location.DENMARK:
            return {
                budgetTotal: 18600,
                lunchValue: 22800,
                healthcareValue: 2000,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 1860,
            }
        case Location.SWEDEN:
            return {
                budgetTotal: 26400,
                lunchValue: 22800,
                healthcareValue: 2000,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 2600,
            }
        case Location.GERMANY:
            return {
                budgetTotal: 2500,
                lunchValue: 2230,
                healthcareValue: 0, // N/A
                maxAdditionalLeaveDays: 7,
                costPerLeaveDay: 250,
            }
        case Location.UK:
            return {
                budgetTotal: 2100,
                lunchValue: 2260,
                healthcareValue: 1500,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 210,
            }
        case Location.SPAIN:
            return {
                budgetTotal: 2500,
                lunchValue: 2250,
                healthcareValue: 528,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 250,
            }
        case Location.PORTUGAL:
            return {
                budgetTotal: 2500,
                lunchValue: 2260,
                healthcareValue: 400,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 250,
            }
        case Location.QUEBEC:
            return {
                budgetTotal: 3700,
                lunchValue: 4480,
                healthcareValue: 1560,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 370,
            }
        case Location.REMOTE:
            return {
                budgetTotal: 2500,
                lunchValue: 2280,
                healthcareValue: 1700,
                maxAdditionalLeaveDays: 10,
                costPerLeaveDay: 250,
            }
        default:
            exhaustiveCheck(location)

            throw new Error('Unknown location')
    }
}

export enum Currency {
    EUR = 'EUR',
    GBP = 'GBP',
    DKK = 'DKK',
    SEK = 'SEK',
    CAD = 'CAD',
}

export const currencySymbolMap: Record<Currency, string> = {
    [Currency.EUR]: '€',
    [Currency.GBP]: '£',
    [Currency.DKK]: 'kr.',
    [Currency.SEK]: 'kr',
    [Currency.CAD]: '$',
}

export const getCurrencyForLocation = (location: Location) => {
    switch (location) {
        case Location.UK:
            return Currency.GBP
        case Location.DENMARK:
            return Currency.DKK
        case Location.SWEDEN:
            return Currency.SEK
        case Location.QUEBEC:
            return Currency.CAD
        default:
            return Currency.EUR
    }
}

export const currencyFormatter = (location: Location) => {
    const numberFormat = new Intl.NumberFormat(getIntlLocaleForLocation(location), {
        style: 'currency',
        currency: getCurrencyForLocation(location),
        currencyDisplay: 'symbol',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    })

    return numberFormat.format
}

type GetReplacePlaceholdersArgs = {
    location: Location
    budgetTotal: number
    budgetUsed: number
    locationNames: LocationNames
}

export const getReplacePlaceholders = ({
    location,
    budgetTotal,
    budgetUsed,
    locationNames,
}: GetReplacePlaceholdersArgs) => {
    return (content: string) => {
        const formatCurrency = currencyFormatter(location)
        const currency = getCurrencyForLocation(location)
        const values = getValuesForLocation(location)

        const valueReplaceMap = {
            '{{budgetTotal}}': formatCurrency(budgetTotal),
            '{{budgetUsed}}': formatCurrency(budgetUsed),
            '{{currencySymbol}}': currencySymbolMap[currency],
            '{{location}}': getLocationName(location, locationNames),
            '{{costPerLeaveDay}}': formatCurrency(values.costPerLeaveDay),
            '{{numberOfLeaveDays}}': getValuesForLocation(location).maxAdditionalLeaveDays,
        }

        return replacePlaceholders(content, valueReplaceMap)
    }
}
